import React, { useState, useEffect } from 'react';
import { API_URL } from '../config/constants';
import { notification, Table, Upload, Modal as AntModal } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { UploadFile } from 'antd/es/upload/interface';

interface ProductCodeForm {
  productName: string;
  productType: 'örme' | 'dokuma';
  photos?: string[];
}

interface ProductCode {
  _id: string;
  productName: string;
  productType: string;
  productCode: string;
  createdAt: string;
}

const ProductCodeGenerator: React.FC = () => {
  const [form, setForm] = useState<ProductCodeForm>({
    productName: '',
    productType: 'örme'
  });
  const [generatedCode, setGeneratedCode] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [productCodes, setProductCodes] = useState<ProductCode[]>([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  useEffect(() => {
    fetchProductCodes();
  }, []);

  const fetchProductCodes = async () => {
    setTableLoading(true);
    try {
      const response = await fetch(`${API_URL}/product-codes`);
      if (!response.ok) throw new Error('Kodlar yüklenirken hata oluştu');
      const data = await response.json();
      setProductCodes(data);
    } catch (error) {
      notification.error({
        message: 'Hata',
        description: 'Ürün kodları yüklenirken bir hata oluştu'
      });
    } finally {
      setTableLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Önce fotoğrafları yükle
      const uploadedPhotos: string[] = [];
      
      if (fileList.length > 0) {
        const formData = new FormData();
        fileList.forEach(file => {
          if (file.originFileObj) {
            formData.append('photos', file.originFileObj);
          }
        });

        const photoResponse = await fetch(`${API_URL}/product-codes/upload-photos`, {
          method: 'POST',
          body: formData,
        });

        if (!photoResponse.ok) {
          throw new Error('Fotoğraflar yüklenirken hata oluştu');
        }

        const photoData = await photoResponse.json();
        uploadedPhotos.push(...photoData.photoUrls);
      }

      // Ürün kodunu oluştur
      const response = await fetch(`${API_URL}/product-codes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...form,
          photos: uploadedPhotos
        }),
      });

      if (!response.ok) {
        throw new Error('Ürün kodu oluşturulurken bir hata oluştu');
      }

      const data = await response.json();
      setGeneratedCode(data.productCode);
      notification.success({
        message: 'Başarılı',
        description: 'Ürün kodu başarıyla oluşturuldu',
      });
      fetchProductCodes();
    } catch (error) {
      notification.error({
        message: 'Hata',
        description: 'Ürün kodu oluşturulurken bir hata oluştu',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleFileChange = ({ fileList: newFileList }: { fileList: UploadFile[] }) => {
    setFileList(newFileList);
  };

  const columns: ColumnsType<ProductCode> = [
    {
      title: 'Ürün Kodu',
      dataIndex: 'productCode',
      key: 'productCode',
    },
    {
      title: 'Ürün Adı',
      dataIndex: 'productName',
      key: 'productName',
    },
    {
      title: 'Ürün Tipi',
      dataIndex: 'productType',
      key: 'productType',
      render: (type) => type === 'örme' ? 'Örme' : 'Dokuma'
    },
    {
      title: 'Oluşturulma Tarihi',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => new Date(date).toLocaleDateString('tr-TR')
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_, record) => (
        <button
          onClick={() => handleDelete(record._id)}
          className="text-red-600 hover:text-red-800"
        >
          <DeleteOutlined /> Sil
        </button>
      ),
    }
  ];

  const handleDelete = (id: string) => {
    AntModal.confirm({
      title: 'Ürün Kodunu Sil',
      content: 'Bu ürün kodunu silmek istediğinizden emin misiniz?',
      okText: 'Evet',
      cancelText: 'Hayır',
      okType: 'danger',
      async onOk() {
        try {
          const response = await fetch(`${API_URL}/product-codes/${id}`, {
            method: 'DELETE',
          });

          if (!response.ok) throw new Error('Silme işlemi başarısız oldu');

          notification.success({
            message: 'Başarılı',
            description: 'Ürün kodu başarıyla silindi',
          });

          // Listeyi güncelle
          fetchProductCodes();
        } catch (error) {
          notification.error({
            message: 'Hata',
            description: 'Ürün kodu silinirken bir hata oluştu',
          });
        }
      },
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">
            Ürün Kodu Oluştur
          </h1>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ürün Adı
              </label>
              <input
                type="text"
                name="productName"
                value={form.productName}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ürün Tipi
              </label>
              <select
                name="productType"
                value={form.productType}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="örme">Örme</option>
                <option value="dokuma">Dokuma</option>
              </select>
            </div>

            {/* Fotoğraf yükleme alanı */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ürün Fotoğrafları (Opsiyonel)
              </label>
              <Upload
                listType="picture-card"
                fileList={fileList}
                onChange={handleFileChange}
                beforeUpload={() => false}
                multiple
              >
                {fileList.length >= 8 ? null : (
                  <div>
                    <PlusOutlined />
                    <div style={{ marginTop: 8 }}>Yükle</div>
                  </div>
                )}
              </Upload>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {loading ? 'Oluşturuluyor...' : 'Kod Oluştur'}
              </button>
            </div>
          </form>

          {generatedCode && (
            <div className="mt-8 p-4 bg-green-50 rounded-lg">
              <h2 className="text-lg font-medium text-green-800 mb-2">
                Oluşturulan Kod:
              </h2>
              <div className="text-2xl font-mono bg-white p-4 rounded border border-green-200">
                {generatedCode}
              </div>
            </div>
          )}

          <div className="mt-8">
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              Oluşturulan Kodlar
            </h2>
            <Table 
              columns={columns} 
              dataSource={productCodes}
              rowKey="_id"
              loading={tableLoading}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `Toplam ${total} kayıt`
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductCodeGenerator;
